import React from "react";
import { Phone, Mail, MapPin, Instagram, MessageCircle } from "lucide-react";
import { COMPANY, NAV_LINKS, SERVICES } from "@/data/site";

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-slate-950 text-white">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <img
                src={COMPANY.logo}
                alt={COMPANY.name}
                className="h-14 w-14 object-contain"
              />
              <div>
                <div className="font-display font-medium">{COMPANY.shortName}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">
                  Glass & Aluminium Installation LLC
                </div>
              </div>
            </div>
            <p className="mt-6 text-white/60 text-sm leading-relaxed max-w-sm">
              Premium glass, aluminum, mirror and joinery solutions delivered across the United Arab
              Emirates with precision, speed and warranty-backed craftsmanship.
            </p>

            <div className="mt-8 flex gap-3" data-testid="footer-socials">
              <a
                href={COMPANY.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="social-instagram"
                aria-label="Follow us on Instagram"
                className="h-10 px-4 border border-white/20 flex items-center gap-2 hover:bg-white hover:text-slate-950 transition-colors"
              >
                <Instagram className="h-4 w-4" />
                <span className="text-xs font-medium">{COMPANY.instagramHandle}</span>
              </a>
              <a
                href={`https://wa.me/${COMPANY.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                data-testid="social-whatsapp"
                aria-label="Chat with us on WhatsApp"
                className="h-10 w-10 border border-white/20 flex items-center justify-center hover:bg-white hover:text-slate-950 transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mb-5">
              Quick links
            </div>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <button
                    onClick={() => scrollTo(l.href)}
                    className="text-white/80 hover:text-white text-sm"
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mb-5">
              Popular Services
            </div>
            <ul className="space-y-3">
              {SERVICES.slice(0, 8).map((s) => (
                <li key={s.name}>
                  <button
                    onClick={() => scrollTo("#services")}
                    className="text-white/80 hover:text-white text-sm text-left"
                  >
                    {s.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mb-5">
              Contact
            </div>
            <ul className="space-y-4 text-sm text-white/80">
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-blue-400" />
                <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-white">
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-blue-400" />
                <a href={`mailto:${COMPANY.email}`} className="hover:text-white break-all">
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-blue-400" />
                <span>{COMPANY.location}</span>
              </li>
            </ul>
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">Business Hours</div>
              <div className="mt-2 text-sm text-white/80">{COMPANY.hours}</div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="text-xs text-white/50">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </div>
          <div className="text-xs text-white/50">
            Serving Dubai · Abu Dhabi · Sharjah · Ajman · RAK · Fujairah · UAQ
          </div>
        </div>
      </div>
    </footer>
  );
};
